import StrategyBulbIcon from './StrategyBulbIcon.jsx';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import Select from "../../utils/Select.jsx";
import DataTable, { createTheme } from 'react-data-table-component';
import { FaPlay, FaStop, FaPencilAlt, FaTrash, FaTimesCircle, FaChevronDown, FaEllipsisV, FaEye, FaSync } from 'react-icons/fa';
import { Ripple } from '../../utils/PopupRipple.jsx';
import 'react-ripple-click/dist/index.css';
import s from './StrategiesTab.module.scss';
import './eaTheme.scss';
import '../account_manager/Positions.css';
import { eaApi, eaErrorMessage, eaNotifyChanged, EA_CHANGED_EVENT, EA_SETTINGS_CHANGED_EVENT } from './eaApi.js';
import { indicatorLabel, tfDisplay, conditionText, fromServerRule } from './eaModel.js';
import StrategyModal from './StrategyModal.jsx';
import { useAuthContext } from '../../../contexts/Auth-Context.js';
import { useChartContext } from '../../../contexts/Chart-Context.js';
import { useMetricsContext } from '../../../contexts/Metrics-Context.js';
import { useAccountManagerContext } from '../../../contexts/Account-Manager-Context.js';
import { adjustDateTime, formatDate, formatFilterDate, formatFilterDateOrPlaceholder } from '../../../utils/format.js';
import AppDatePicker, { toLocalYmd } from '../../common/AppDatePicker.jsx';
import CellText from '../account_manager/CellText.jsx';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import NoRecordsMessage from '../../utils/NoRecordsMessage.jsx';
import { dropdownOptionStyle } from '../../../utils/dropdownOptionStyles.js';
import { getEaPeriodOptions } from '../../../i18n/periodOptions.js';
import useTableHScrollPin from '../account_manager/useTableHScrollPin.js';

const PERIOD_CONTROL_WIDTH = 140;

const PERIOD_MENU_PREFIX = 'history-period';
const STRATEGY_TAB_SELECTOR = '#ea-strategies';
const STRATEGY_TAB_SCROLLER_SELECTOR = '#ea-strategies .table-scroll-area';

function getStrategyTabClipRect() {
  return document.querySelector(STRATEGY_TAB_SELECTOR)?.getBoundingClientRect() ?? null;
}

function isRectEscapingTab(rect, tabRect) {
  if (!rect || !tabRect) return false;
  return (
    rect.bottom > tabRect.bottom
    || rect.top < tabRect.top
    || rect.right > tabRect.right
    || rect.left < tabRect.left
  );
}

function shouldCloseFloatingMenuOnTabScroll(anchorEl, menuEl) {
  const tabRect = getStrategyTabClipRect();
  if (!tabRect) return false;

  if (anchorEl && isRectEscapingTab(anchorEl.getBoundingClientRect(), tabRect)) {
    return true;
  }

  if (menuEl && isRectEscapingTab(menuEl.getBoundingClientRect(), tabRect)) {
    return true;
  }

  return false;
}

function isScrollInsidePeriodMenu(event) {
  const target = event.target;
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest(`.${PERIOD_MENU_PREFIX}__menu-list`)
    || target.closest(`.${PERIOD_MENU_PREFIX}__menu`),
  );
}

function isStrategyTabTableScroll(event) {
  const target = event.target;
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest(STRATEGY_TAB_SCROLLER_SELECTOR));
}

function shouldClosePeriodMenuOnScroll(event) {
  if (isScrollInsidePeriodMenu(event)) return false;
  if (!isStrategyTabTableScroll(event)) return false;

  const menu = document.querySelector(`.${PERIOD_MENU_PREFIX}__menu`);
  if (!menu) return false;

  const openControl = document.querySelector(
    `${STRATEGY_TAB_SELECTOR} .${PERIOD_MENU_PREFIX}__control[aria-expanded="true"]`,
  );

  return shouldCloseFloatingMenuOnTabScroll(openControl, menu);
}

function ymd(date) {
  return toLocalYmd(date);
}

function sqlDayStart(day) {
  return `${day} 00:00:00`;
}

function sqlDayEnd(day) {
  return `${day} 23:59:59`;
}

function ymdRangeForPeriod(value) {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (value === 'Yesterday') {
    start.setDate(start.getDate() - 1);
    end.setDate(end.getDate() - 1);
  } else if (value === 'CurrentWeek') {
    start.setDate(start.getDate() - start.getDay());
    end.setTime(start.getTime());
    end.setDate(start.getDate() + 6);
  } else if (value === 'CurrentMonth') {
    start.setDate(1);
    end.setMonth(end.getMonth() + 1, 0);
  } else if (value === 'PreviousMonth') {
    start.setMonth(start.getMonth() - 1, 1);
    end.setFullYear(start.getFullYear(), start.getMonth() + 1, 0);
  } else if (value === 'LastThreeMonth') {
    start.setMonth(start.getMonth() - 2, 1);
  } else if (value === 'Last6Month') {
    start.setMonth(start.getMonth() - 5, 1);
  } else if (value === 'all') {
    start.setFullYear(2000, 0, 1);
  }
  return { start: ymd(start), end: ymd(end) };
}

function ruleText(rule, indicators) {
  const picked = (indicators || []).map((i) => ({ alias: i.alias, code: i.code, params: i.params }));
  const rows = fromServerRule(rule, picked.map((p) => p.alias));
  if (!rows.length) return '—';
  const join = rule?.logic === 'AND' ? ` ${i18n.t('ea.and')} ` : ` ${i18n.t('ea.or')} `;
  return rows.map((c) => conditionText(c, picked, i18n.t.bind(i18n))).join(join);
}

function eventLabel(ev) {
  return {
    entry: i18n.t('ea.eventEntry'),
    exit: i18n.t('ea.eventExit'),
    skip: i18n.t('ea.eventSkipped'),
    error: i18n.t('ea.eventError'),
  }[ev] || ev;
}

function journalDetail(j) {
  const p = j.payload || {};
  if (p.reason) return String(p.reason);
  if (p.error) return String(p.error);
  if (p.message) return String(p.message);
  if (j.event === 'entry' || j.event === 'exit') {
    const parts = [];
    const latest = p.latestByAlias || {};
    for (const [alias, val] of Object.entries(latest)) {
      if (val != null && Number.isFinite(Number(val))) {
        parts.push(`${alias} ${Number(val).toFixed(2)}`);
      }
    }
    if (p.close != null && Number.isFinite(Number(p.close))) parts.push(`close ${Number(p.close).toFixed(5)}`);
    if (p.openRes?.error) parts.push(String(p.openRes.error));
    if (p.closeRes?.error) parts.push(String(p.closeRes.error));
    return parts.join(' · ');
  }
  return '';
}

const EVENT_CLASS = {
  entry: s.ev_entry,
  exit: s.ev_exit,
  error: s.ev_error,
  skip: s.ev_skip,
};

function buildPeriodSelectStyle(buyColor) {
  const w = PERIOD_CONTROL_WIDTH;
  return {
    control: (provided, state) => ({
      ...provided,
      minHeight: '24px',
      height: '24px',
      width: w,
      minWidth: w,
      maxWidth: w,
      margin: 0,
      borderColor: state.isFocused ? `${buyColor} !important` : 'var(--surface-border, #3a4156) !important',
      backgroundColor: 'var(--surface-panel, #252b42) !important',
      borderRadius: '4px',
      boxShadow: 'none',
      cursor: 'pointer',
      alignItems: 'center',
      boxSizing: 'border-box',
      '&:hover': { borderColor: `${buyColor} !important`, boxShadow: 'none' },
    }),
    singleValue: (provided) => ({
      ...provided,
      paddingLeft: '0',
      margin: '0',
      color: 'var(--content-secondary, #b8bcc8) !important',
      fontSize: '11px',
      lineHeight: '1',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 2px 0 6px',
      height: '22px',
      display: 'flex',
      alignItems: 'center',
    }),
    input: (provided) => ({ ...provided, margin: 0, padding: 0 }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: '22px',
      alignSelf: 'center',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      padding: '0 4px',
      color: state.isFocused || state.selectProps.menuIsOpen
        ? `${buyColor} !important`
        : 'var(--content-secondary, #b8bcc8) !important',
      transition: 'color 0.15s ease',
      transform: 'none',
      '&:hover': { color: `${buyColor} !important` },
      svg: {
        width: '14px',
        height: '14px',
        transition: 'transform 0.2s ease',
        transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        transformOrigin: 'center',
      },
    }),
    option: (provided, state) => dropdownOptionStyle(provided, state, {
      padding: '0 12px',
      height: '24px',
      minHeight: '24px',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      fontSize: '11px',
      lineHeight: '1.35',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'var(--surface-panel, #252b42) !important',
      margin: '4px 0 0 0 !important',
      zIndex: 10060,
      overflow: 'hidden',
      borderRadius: '6px',
      border: '1px solid var(--surface-border, #3a4156)',
      boxShadow: 'none',
      width: w,
      minWidth: w,
      maxWidth: w,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '132px',
      minHeight: '3px',
      width: '100%',
      overflowY: 'auto',
      padding: '2px 0',
    }),
    menuPortal: (provided) => ({ ...provided, zIndex: 10060 }),
  };
}

function ActionMenu({
  r, busy, globalOn, buyColor = '#21C46D', sellColor = '#E13232',
  onView, onStart, onStop, onClose, onEdit, onDelete,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const enabled = r.status === 'enabled';

  const placeMenu = useCallback(() => {
    const btn = btnRef.current;
    const menu = menuRef.current;
    if (!btn || !menu) return;
    const br = btn.getBoundingClientRect();
    const mh = menu.offsetHeight || 200;
    const mw = menu.offsetWidth || 160;
    const gap = 4;
    const pad = 8;

    let top = br.bottom + gap;
    if (top + mh > window.innerHeight - pad) {
      top = br.top - mh - gap;
    }
    if (top < pad) top = Math.min(br.bottom + gap, window.innerHeight - mh - pad);
    top = Math.max(pad, Math.min(top, window.innerHeight - mh - pad));

    let left = br.right - mw;
    left = Math.max(pad, Math.min(left, window.innerWidth - mw - pad));

    setCoords((prev) => (prev.top === top && prev.left === left ? prev : { top, left }));
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    placeMenu();
    return undefined;
  }, [open, placeMenu]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = (event) => {
      const target = event.target;
      if (target instanceof Element && menuRef.current?.contains(target)) return;
      if (shouldCloseFloatingMenuOnTabScroll(btnRef.current, menuRef.current)) {
        setOpen(false);
      }
    };
    const scroller = btnRef.current?.closest?.('.table-scroll-area');

    const t = window.setTimeout(() => {
      document.addEventListener('pointerdown', onDoc, true);
    }, 0);
    document.addEventListener('keydown', onKey);
    // Only the table scroller + window resize — not capture on every nested scroll
    scroller?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('pointerdown', onDoc, true);
      document.removeEventListener('keydown', onKey);
      scroller?.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const act = (fn) => { setOpen(false); fn?.(); };

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    const br = btnRef.current?.getBoundingClientRect();
    if (br) {
      setCoords({ top: br.bottom + 4, left: Math.max(8, br.right - 160) });
    }
    setOpen(true);
  };

  const menu = open && createPortal(
    <div
      ref={menuRef}
      className={s.menuDropdown}
      style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        right: 'auto',
        bottom: 'auto',
        zIndex: 10050,
        ['--buy-color']: buyColor,
        ['--sell-color']: sellColor,
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <button type="button" className={s.menuItem} onClick={() => act(onView)}>
        <FaEye className={s.menuIcon} />
        <span>{t('ea.view')}</span>
      </button>

          {enabled ? (
        <button type="button" className={`${s.menuItem} ${s.menuItemDanger}`} onClick={() => act(onStop)}>
              <FaStop className={s.menuIcon} />
          <span>{t('ea.stop')}</span>
            </button>
          ) : (
            <button
              type="button"
              className={`${s.menuItem} ${s.menuItemGo}`}
              disabled={!globalOn}
              onClick={() => act(onStart)}
          title={globalOn ? undefined : t('ea.tradingOffPlatformShort')}
            >
              <FaPlay className={s.menuIcon} />
          <span>{t('ea.start')}</span>
            </button>
          )}

      <button type="button" className={`${s.menuItem} ${s.menuItemDanger}`} onClick={() => act(onClose)}>
            <FaTimesCircle className={s.menuIcon} />
        <span>{t('ea.closePosition')}</span>
          </button>

          <div className={s.menuDivider} />

          <button
            type="button"
            className={s.menuItem}
            disabled={enabled}
            onClick={() => act(onEdit)}
        title={enabled ? t('ea.stopBeforeEdit') : undefined}
          >
            <FaPencilAlt className={s.menuIcon} />
        <span>{t('ea.edit')}</span>
          </button>

          <button
            type="button"
            className={`${s.menuItem} ${s.menuItemDanger}`}
            disabled={enabled}
            onClick={() => act(onDelete)}
        title={enabled ? t('ea.stopBeforeDelete') : undefined}
          >
            <FaTrash className={s.menuIcon} />
        <span>{t('ea.delete')}</span>
          </button>
    </div>,
    document.body,
  );

  return (
    <div className={s.menuWrap}>
      <button
        ref={btnRef}
        type="button"
        className={`${s.menuTrigger}${open ? ` ${s.menuTriggerActive}` : ''}`}
        onClick={toggle}
        onMouseDown={(e) => e.stopPropagation()}
        disabled={busy}
        title={t('ea.actionsCol')}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <FaEllipsisV />
      </button>
      {menu}
    </div>
  );
}

createTheme('solarized', {
  text: { primary: 'var(--content-secondary, #b8bcc8)' },
  background: { default: 'var(--surface-panel, #252b42)' },
  context: { background: 'var(--surface-panel, #252b42)', text: 'var(--content-secondary, #b8bcc8)' },
}, 'dark');

const STRATEGY_TABLE_STYLES = {
  table: {
    style: {
      width: '100%',
      minWidth: 'fit-content',
    },
  },
  headCells: {
    style: {
      paddingLeft: '4px !important',
      paddingRight: '4px !important',
      justifyContent: 'flex-start !important',
      alignItems: 'center !important',
      textAlign: 'left !important',
    },
  },
  cells: {
    style: {
      paddingLeft: '4px !important',
      paddingRight: '4px !important',
      justifyContent: 'flex-start !important',
      alignItems: 'center !important',
      textAlign: 'left !important',
    },
  },
  rows: {
    style: {
      cursor: 'pointer',
    },
  },
  headRow: {
    style: {
      minHeight: '10px',
      height: '30px',
      backgroundColor: 'var(--surface-panel, #252b42)',
    },
    denseStyle: {
      minHeight: '10px',
      height: '30px',
    },
  },
  expanderCell: {
    style: {
      display: 'none !important',
      width: '0px !important',
      minWidth: '0px !important',
      maxWidth: '0px !important',
      padding: '0px !important',
      flex: '0 0 0 !important',
    },
  },
  expanderButton: {
    style: { display: 'none !important' },
  },
  expanderRow: {
    style: { backgroundColor: 'var(--surface-bg, #1a1f36)' },
  },
};


// Self-contained journal UI (local state) — used as RDT expandableRowsComponent
// so expand sits under the row without remounting on every parent keystroke.
function StrategyJournalPanel({ data: strategy }) {
  const { t } = useTranslation();
  const periodOptions = useMemo(() => getEaPeriodOptions(t), [t]);
  const { platFromData } = useAuthContext();
  const { selectedStyle } = useChartContext();
  const { utcOffset } = useMetricsContext();
  const userId = platFromData?.[5]?.userId;
  const buyColor = selectedStyle?.buyColor || '#00d97e';
  const r = strategy;

  const [journal, setJournal] = useState([]);
  const [journalTotal, setJournalTotal] = useState(0);
  const [journalLoading, setJournalLoading] = useState(false);
  const [journalSearch, setJournalSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [journalPeriod, setJournalPeriod] = useState('Today');
  const [customStart, setCustomStart] = useState(() => ymd(new Date()));
  const [customEnd, setCustomEnd] = useState(() => ymd(new Date()));
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickerAnchorRect, setPickerAnchorRect] = useState(null);
  const [activeDateField, setActiveDateField] = useState('start');

  const selectedJournalPeriod =
    periodOptions.find((o) => o.value === journalPeriod) || periodOptions[1];
  const periodSelectStyle = React.useMemo(() => buildPeriodSelectStyle(buyColor), [buyColor]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(journalSearch.trim()), 400);
    return () => clearTimeout(t);
  }, [journalSearch]);

  const journalFilters = useCallback(() => {
    if (journalPeriod === 'Custom' && (!customStart || !customEnd)) return null;
    const range = journalPeriod === 'Custom'
      ? { start: customStart, end: customEnd }
      : ymdRangeForPeriod(journalPeriod);
    return {
      startDate: sqlDayStart(range.start),
      endDate: sqlDayEnd(range.end),
      search: debouncedSearch,
      pageSize: 100,
    };
  }, [journalPeriod, customStart, customEnd, debouncedSearch]);

  const refreshJournal = useCallback(async ({ showSpinner } = {}) => {
    if (!userId || !r?.id) return;
    const filters = journalFilters();
    if (!filters) return;
    if (showSpinner) setJournalLoading(true);
    try {
      const rt = await eaApi.runtime(r.id, userId, filters);
      setJournal(rt?.journal || []);
      setJournalTotal(Number(rt?.total) || 0);
    } catch (e) {
      if (showSpinner) toast.error(eaErrorMessage(e, t('ea.couldNotLoadHistory')));
    } finally {
      if (showSpinner) setJournalLoading(false);
    }
  }, [r?.id, userId, journalFilters, t]);

  useEffect(() => {
    let timer = null;
    refreshJournal({ showSpinner: true });
    const onChanged = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => refreshJournal(), 150);
    };
    const topWin = (() => { try { return window.top; } catch { return window; } })();
    topWin.addEventListener(EA_CHANGED_EVENT, onChanged);
    return () => {
      if (timer) clearTimeout(timer);
      topWin.removeEventListener(EA_CHANGED_EVENT, onChanged);
    };
  }, [refreshJournal]);

  const handleJournalPeriodChange = useCallback((option) => {
    if (!option) return;
    const next = option.value;
    setJournalPeriod(next);
    if (next === 'Custom') {
      setCustomStart('');
      setCustomEnd('');
    } else {
      const range = ymdRangeForPeriod(next);
      setCustomStart(range.start);
      setCustomEnd(range.end);
    }
  }, []);

  const openCustomDatePicker = (field, e) => {
    setActiveDateField(field);
    const rect = e?.currentTarget?.getBoundingClientRect?.();
    if (rect) {
      setPickerAnchorRect({
        top: rect.top,
        left: rect.left,
        bottom: rect.bottom,
        right: rect.right,
        width: rect.width,
        height: rect.height,
      });
    } else {
      setPickerAnchorRect(null);
    }
    setShowDatePicker(true);
  };

  return (
    <div
      className={s.detail}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className={s.summary}>
      <div className={s.metaBar}>
        <div className={s.metaItem}>
          <span className={s.metaLabel}>{t('tables.createdAt')}:</span>
          <span className={s.metaVal}>
            {r.created_at ? formatDate(adjustDateTime(r.created_at, utcOffset)) : '—'}
          </span>
        </div>
        <div className={s.metaItem}>
          <span className={s.metaLabel}>{t('ea.updatedAt')}:</span>
          <span className={s.metaVal}>
            {r.updated_at ? formatDate(adjustDateTime(r.updated_at, utcOffset)) : '—'}
          </span>
        </div>
      </div>

      <div className={s.rules}>
        <div className={s.ruleRow}>
          <span className={s.ruleLabel}>{t('ea.entryRule')}</span>{' '}
          <span>
            {ruleText(r.entry_rule, r.indicators)} →{' '}
            <span className={String(r.entry_rule?.action || 'BUY').toUpperCase() === 'SELL' ? s.ev_error : s.ev_entry}>
              {String(r.entry_rule?.action || 'BUY').toUpperCase()}
            </span>
          </span>
        </div>
        <div className={s.ruleRow}>
          <span className={s.ruleLabel}>{t('ea.exitLabel')}</span>{' '}
          <span>
            {Number(r.exit_rule_enabled)
              ? ruleText(r.exit_rule, r.indicators)
              : t('ea.exitDisabledHint')}
          </span>
        </div>
      </div>

      </div>
      <div className={s.journalHead}>
        <div className={s.journalTitle}>{t('ea.history')}</div>
        {journalTotal > 0 && <span className={s.journalCount}>{journalTotal}</span>}
      </div>
      <div className={`${s.journalFilters} history-filters`}>
        <div className="hf-group hf-search">
          <div className="input-text">
            <input
              type="text"
              value={journalSearch}
              onChange={(e) => setJournalSearch(e.target.value)}
              placeholder={t('tables.searchSymbol')}
              aria-label={t('ea.searchHistory')}
            />
            <div className="input-svg" id="searchSvgColor">
              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" fill="currentColor" className="bi bi-search" viewBox="0 0 16 16">
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0" />
              </svg>
            </div>
          </div>
        </div>
        <div className="hf-group hf-time">
          <div className="hf-preset">
            <span className="hf-label">{t('filters.period')}</span>
            <Select
              options={periodOptions}
              value={selectedJournalPeriod}
              onChange={handleJournalPeriodChange}
              styles={periodSelectStyle}
              isSearchable={false}
              classNamePrefix="history-period"
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPosition="fixed"
              menuPlacement="auto"
              menuShouldScrollIntoView={false}
              closeMenuOnScroll={shouldClosePeriodMenuOnScroll}
            />
          </div>
          {journalPeriod === 'Custom' && (
            <div className="hf-custom date-time">
              <button
                type="button"
                className={`custom-date-button${showDatePicker && activeDateField === 'start' ? ' is-active' : ''}`}
                title={formatFilterDateOrPlaceholder(customStart ? new Date(`${customStart}T00:00:00`) : null)}
                onClick={(e) => {
                  e.stopPropagation();
                  openCustomDatePicker('start', e);
                }}
              >
                {formatFilterDateOrPlaceholder(customStart ? new Date(`${customStart}T00:00:00`) : null)}
              </button>
              <span>–</span>
              <button
                type="button"
                className={`custom-date-button${showDatePicker && activeDateField === 'end' ? ' is-active' : ''}`}
                title={formatFilterDateOrPlaceholder(customEnd ? new Date(`${customEnd}T00:00:00`) : null)}
                onClick={(e) => {
                  e.stopPropagation();
                  openCustomDatePicker('end', e);
                }}
              >
                {formatFilterDateOrPlaceholder(customEnd ? new Date(`${customEnd}T00:00:00`) : null)}
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          className={`${s.journalRefreshBtn} ea-history-refresh`}
          disabled={journalLoading}
          title={t('ea.refreshHistory')}
          aria-label={t('ea.refreshHistory')}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            refreshJournal({ showSpinner: true });
          }}
        >
          <FaSync className={journalLoading ? s.journalRefreshSpin : undefined} />
          <span>{t('ea.refreshHistory')}</span>
        </button>
      </div>
      {journalLoading && <div className={s.empty}>{t('ea.loadingStrategies')}</div>}
      {!journalLoading && journal.length === 0 && (
        <div className={s.empty}>{t('ea.nothingInRange')}</div>
      )}
      {!journalLoading && journal.length > 0 && (
        <table className={s.journalTable}>
          <thead>
            <tr>
              <th>{t('ea.time')}</th>
              <th>{t('ea.event')}</th>
              <th>{t('ea.detail')}</th>
            </tr>
          </thead>
          <tbody>
            {journal.map((j) => (
              <tr key={`${j.created_at}-${j.event}-${j.position_id || ''}`}>
                <td className={s.jTime}>{formatDate(adjustDateTime(j.created_at, utcOffset))}</td>
                <td>
                  <span className={EVENT_CLASS[j.event] || s.ev_skip}>{eventLabel(j.event)}</span>
                </td>
                <td className={s.jPayload}>{journalDetail(j) || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showDatePicker && (
        <AppDatePicker
          open
          activeDateField={activeDateField}
          value={activeDateField === 'start' ? customStart : customEnd}
          minDate={activeDateField === 'end' && customStart ? customStart : undefined}
          anchorRect={pickerAnchorRect}
          buyColor={buyColor}
          sellColor={selectedStyle?.sellColor || '#E13232'}
          onCancel={() => setShowDatePicker(false)}
          onConfirm={(date) => {
            const day = toLocalYmd(date);
            setJournalPeriod('Custom');
            if (activeDateField === 'start') {
              setCustomStart(day);
              if (customEnd && day > customEnd) setCustomEnd(day);
            } else {
              setCustomEnd(day);
            }
            setShowDatePicker(false);
          }}
        />
      )}
    </div>
  );
}

export default function StrategiesTab() {
  const { t } = useTranslation();
  const periodOptions = useMemo(() => getEaPeriodOptions(t), [t]);
  const { platFromData } = useAuthContext();
  const { selectedStyle } = useChartContext();
  const { utcOffset } = useMetricsContext();
  const { setStrategiesCount } = useAccountManagerContext();
  const userId = platFromData?.[5]?.userId;
  const buyColor = selectedStyle?.buyColor || '#00d97e';
  const sellColor = selectedStyle?.sellColor || '#ff4757';
  const accentVars = {
    '--ea-accent': buyColor,
    '--buy-color': buyColor,
    '--sell-color': sellColor,
  };

  const [rows, setRows] = useState([]);
  const [globalOn, setGlobalOn] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderStrategy, setBuilderStrategy] = useState(null);
  const [builderReadOnly, setBuilderReadOnly] = useState(false);

  const [listSearch, setListSearch] = useState('');
  const [debouncedListSearch, setDebouncedListSearch] = useState('');
  const [listPeriod, setListPeriod] = useState('all');
  const [listStart, setListStart] = useState(() => ymd(new Date('2000-01-01')));
  const [listEnd, setListEnd] = useState(() => ymd(new Date()));

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickerAnchorRect, setPickerAnchorRect] = useState(null);
  const [activeDateField, setActiveDateField] = useState('start');
  const tableScrollRef = useRef(null);

  useTableHScrollPin(tableScrollRef, !loading && rows.length > 0);

  const selectedListPeriod =
    periodOptions.find((o) => o.value === listPeriod) || periodOptions[0];

  const periodSelectStyle = React.useMemo(() => buildPeriodSelectStyle(buyColor), [buyColor]);

  const openCustomDatePicker = useCallback((field, e) => {
    setActiveDateField(field);
    const rect = e?.currentTarget?.getBoundingClientRect?.();
    if (rect) {
      setPickerAnchorRect({
        top: rect.top,
        left: rect.left,
        bottom: rect.bottom,
        right: rect.right,
        width: rect.width,
        height: rect.height,
      });
    } else {
      setPickerAnchorRect(null);
    }
    setShowDatePicker(true);
  }, []);

  const handleListPeriodChange = useCallback((option) => {
    if (!option) return;
    const next = option.value;
    setListPeriod(next);
    if (next === 'Custom') {
      setListStart('');
      setListEnd('');
    } else {
      const range = ymdRangeForPeriod(next);
      setListStart(range.start);
      setListEnd(range.end);
    }
  }, []);

  const load = useCallback(async ({ showSpinner, settingsOnly } = {}) => {
    if (!userId) return;
    if (showSpinner) setLoading(true);
    try {
      if (settingsOnly) {
        const settings = await eaApi.settings();
        setGlobalOn(Number(settings?.is_ea_globally_enabled) === 1);
        return;
      }
      const [list, settings] = await Promise.all([eaApi.list(userId), eaApi.settings()]);
      const next = list || [];
      setRows(next);
      setStrategiesCount(next.length);
      setGlobalOn(Number(settings?.is_ea_globally_enabled) === 1);
    } catch (e) {
      toast.error(eaErrorMessage(e, t('ea.couldNotLoadStrategies')));
    } finally {
      setLoading(false);
    }
  }, [userId, setStrategiesCount, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedListSearch(listSearch.trim()), 400);
    return () => clearTimeout(t);
  }, [listSearch]);

  useEffect(() => {
    // Strategy CRUD / entry-exit → refresh list only (journal panel listens separately).
    // Settings toggle → light settings fetch (do not re-hit strategies on every userDetailsInfo).
    let listTimer = null;
    let settingsTimer = null;
    const onStrategies = () => {
      if (listTimer) clearTimeout(listTimer);
      listTimer = setTimeout(() => load(), 150);
    };
    const onSettings = () => {
      if (settingsTimer) clearTimeout(settingsTimer);
      settingsTimer = setTimeout(() => load({ settingsOnly: true }), 150);
    };
    const topWin = (() => { try { return window.top; } catch { return window; } })();
    topWin.addEventListener(EA_CHANGED_EVENT, onStrategies);
    topWin.addEventListener(EA_SETTINGS_CHANGED_EVENT, onSettings);
    return () => {
      if (listTimer) clearTimeout(listTimer);
      if (settingsTimer) clearTimeout(settingsTimer);
      topWin.removeEventListener(EA_CHANGED_EVENT, onStrategies);
      topWin.removeEventListener(EA_SETTINGS_CHANGED_EVENT, onSettings);
    };
  }, [load]);

  const filteredRows = React.useMemo(() => {
    const range = listPeriod === 'Custom'
      ? { start: listStart, end: listEnd }
      : ymdRangeForPeriod(listPeriod);
    const skipDate = listPeriod === 'Custom' && (!listStart || !listEnd);
    const startMs = skipDate ? NaN : new Date(`${range.start}T00:00:00`).getTime();
    const endMs = skipDate ? NaN : new Date(`${range.end}T23:59:59`).getTime();
    const q = debouncedListSearch.toLowerCase();

    return rows.filter((r) => {
      if (q) {
        const hay = `${r.name || ''} ${r.symbol || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      const created = new Date(r.created_at).getTime();
      if (Number.isFinite(created) && (created < startMs || created > endMs)) return false;
      return true;
    });
  }, [rows, listPeriod, listStart, listEnd, debouncedListSearch]);

  const run = async (id, fn, okMsg) => {
    setBusyId(id);
    try {
      await fn();
      toast.success(okMsg);
      eaNotifyChanged();
      await load();
    } catch (e) {
      toast.error(eaErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  const start = (r) => run(r.id, () => eaApi.start(r.id, userId), t('ea.strategyStarted'));
  const stop = (r) => run(r.id, () => eaApi.stop(r.id, userId), t('ea.strategyStopped'));
  const closePos = (r) => run(r.id, () => eaApi.closePosition(r.id, userId), t('ea.positionClosed'));

  const openBuilder = useCallback((strategy, readOnly = false) => {
    setBuilderStrategy(strategy || null);
    setBuilderReadOnly(!!readOnly);
    setBuilderOpen(true);
  }, []);

  const closeBuilder = useCallback(() => {
    setBuilderOpen(false);
    setBuilderStrategy(null);
    setBuilderReadOnly(false);
  }, []);

  const viewStrat = (r) => openBuilder(r, true);
  const editStrat = (r) => openBuilder(r, false);

  const requestDelete = (r) => setDeleteTarget(r);

  const confirmDelete = () => {
    const r = deleteTarget;
    setDeleteTarget(null);
    if (!r?.id) return;
    return run(r.id, () => eaApi.remove(r.id, userId), t('ea.strategyDeleted'));
  };

  const cancelDelete = () => setDeleteTarget(null);

  const toggleJournal = useCallback((id) => {
    setExpanded((prev) => (prev === id ? null : id));
  }, []);

  const strategyRowStyles = React.useMemo(() => [
    {
      when: (row) => expanded === row.id,
      classNames: ['am-row-selected'],
      style: { backgroundColor: 'var(--row-hover, #323a54)', color: 'var(--content-primary, #e8eaf0)' },
    },
  ], [expanded]);

  const tableColumns = React.useMemo(() => [
    {
      id: 'strat-name',
      name: t('ea.name'),
      selector: (r) => r.name,
      minWidth: '108px',
      grow: 2,
      wrap: false,
      cell: (r) => (
        <button
          type="button"
          className={s.nameBtn}
          aria-expanded={expanded === r.id}
          aria-label={r.name}
          onClick={(e) => {
            e.stopPropagation();
            toggleJournal(r.id);
          }}
        >
          <FaChevronDown className={expanded === r.id ? s.caretOpen : s.caret} />
          <span className={s.nameText}><CellText text={r.name} /></span>
        </button>
      ),
    },
    {
      id: 'strat-symbol',
      name: t('tables.symbol'),
      selector: (r) => r.symbol,
      minWidth: '70px',
      grow: 1,
      wrap: false,
      cell: (r) => <CellText text={r.symbol} />,
    },
    {
      id: 'strat-timeframe',
      name: t('ea.timeframeShort'),
      selector: (r) => tfDisplay(r.timeframe),
      minWidth: '44px',
      width: '52px',
      grow: 0,
      wrap: false,
      cell: (r) => <span className={s.cTf}>{tfDisplay(r.timeframe)}</span>,
    },
    {
      id: 'strat-indicators',
      name: t('ea.indicators'),
      selector: (r) => (r.indicators || []).map((i) => indicatorLabel(i)).join(', '),
      minWidth: '80px',
      grow: 2,
      wrap: false,
      cell: (r) => (
        <CellText text={(r.indicators || []).map((i) => indicatorLabel(i)).join(', ') || '—'} />
      ),
    },
    {
      id: 'strat-quantity',
      name: t('ea.quantityShort'),
      selector: (r) => Number(r.quantity ?? r.lot_size),
      minWidth: '54px',
      grow: 1,
      wrap: false,
      cell: (r) => <CellText text={Number(r.quantity ?? r.lot_size)} />,
    },
    {
      id: 'strat-sl',
      name: t('ea.slPnl'),
      selector: (r) => Number(r.stop_loss_pnl) || 0,
      minWidth: '58px',
      width: '68px',
      grow: 0,
      wrap: false,
      cell: (r) => {
        const n = Number(r.stop_loss_pnl);
        return Number.isFinite(n) && n > 0 ? <CellText text={n} /> : '—';
      },
    },
    {
      id: 'strat-tp',
      name: t('ea.tpPnl'),
      selector: (r) => Number(r.take_profit_pnl) || 0,
      minWidth: '58px',
      width: '68px',
      grow: 0,
      wrap: false,
      cell: (r) => {
        const n = Number(r.take_profit_pnl);
        return Number.isFinite(n) && n > 0 ? <CellText text={n} /> : '—';
      },
    },
    {
      id: 'strat-exit-rule',
      name: t('ea.exitRule'),
      selector: (r) => Number(r.exit_rule_enabled),
      minWidth: '54px',
      width: '60px',
      grow: 0,
      wrap: false,
      cell: (r) => (Number(r.exit_rule_enabled) ? t('ea.on') : t('ea.off')),
    },
    {
      id: 'strat-status',
      name: t('tables.status'),
      selector: (r) => r.status,
      minWidth: '76px',
      width: '84px',
      grow: 0,
      wrap: false,
      cell: (r) => {
        const enabled = r.status === 'enabled';
  return (
          <span className={`${s.state} ${enabled ? s.stateRun : r.status === 'disabled' ? s.stateStopped : s.stateDraft}`}>
            <span className={s.led} />
            {enabled ? t('ea.running') : r.status === 'disabled' ? t('ea.stopped') : t('ea.draft')}
          </span>
        );
      },
    },
    {
      id: 'strat-created-at',
      name: t('tables.createdAt'),
      selector: (r) => r.created_at,
      minWidth: '116px',
      width: '124px',
      grow: 0,
      wrap: false,
      cell: (r) => (
        <CellText text={r.created_at ? formatDate(adjustDateTime(r.created_at, utcOffset)) : '—'} />
      ),
    },
    {
      id: 'strat-actions',
      name: '',
      width: '44px',
      minWidth: '44px',
      grow: 0,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      cell: (r) => (
        <ActionMenu
          r={r}
          busy={busyId === r.id}
          globalOn={globalOn}
          buyColor={buyColor}
          sellColor={sellColor}
          onView={() => viewStrat(r)}
          onStart={() => start(r)}
          onStop={() => stop(r)}
          onClose={() => closePos(r)}
          onEdit={() => editStrat(r)}
          onDelete={() => requestDelete(r)}
        />
      ),
    },
  ], [expanded, busyId, globalOn, utcOffset, toggleJournal, buyColor, sellColor, t]);

  if (!userId) return null;

  return (
    <div className={`${s.wrap} ea-strategies-panel app-scrollbar`} style={accentVars}>
      <StrategyModal
        open={builderOpen}
        onClose={closeBuilder}
        userId={userId}
        symbol={builderStrategy?.symbol || ''}
        timeframe={builderStrategy?.timeframe || '5m'}
        editing={builderStrategy}
        readOnly={builderReadOnly}
      />
      {loading && (
        <div className={s.emptyState}>
          <div className={s.emptySpinner} />
          <span>{t('ea.loadingStrategiesList')}</span>
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className={s.emptyState}>
          <StrategyBulbIcon className={s.emptyIcon} />
          <div className={s.emptyTitle}>{t('ea.noStrategiesYet')}</div>
          <div className={s.emptyHint}>
            {t('ea.noStrategiesHint')}
          </div>
          <button
            type="button"
            className={s.newBtn}
            disabled={!globalOn}
            title={globalOn ? undefined : t('ea.tradingOffPlatform')}
            onClick={() => openBuilder(null, false)}
          >
            <StrategyBulbIcon className={s.newBtnIcon} /> {t('ea.strategy')}
          </button>
          {!globalOn && <span className={s.warn}>{t('ea.tradingOffPlatform')}</span>}
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="bottom-table-bstorm">
          <div className="positions-button history-filters">
            <div className="hf-group hf-search">
              <div className="input-text">
                <input
                  type="text"
                  value={listSearch}
                  onChange={(e) => setListSearch(e.target.value)}
                  placeholder={t('tables.searchSymbol')}
                  aria-label={t('ea.searchStrategies')}
                />
                <div className="input-svg" id="searchSvgColor">
                  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" fill="currentColor" className="bi bi-search" viewBox="0 0 16 16">
                    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="hf-group hf-time">
              <div className="hf-preset">
                <span className="hf-label">{t('filters.period')}</span>
                <Select
                  options={periodOptions}
                  value={selectedListPeriod}
                  onChange={handleListPeriodChange}
                  styles={periodSelectStyle}
                  isSearchable={false}
                  classNamePrefix="history-period"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuPlacement="auto"
                  menuShouldScrollIntoView={false}
                  closeMenuOnScroll={shouldClosePeriodMenuOnScroll}
                />
              </div>
              {listPeriod === 'Custom' && (
                <div className="hf-custom date-time">
          <button
            type="button"
                    className={`custom-date-button${showDatePicker && activeDateField === 'start' ? ' is-active' : ''}`}
                    title={formatFilterDateOrPlaceholder(listStart ? new Date(`${listStart}T00:00:00`) : null)}
                    onClick={(e) => {
                      e.stopPropagation();
                      openCustomDatePicker('start', e);
                    }}
                  >
                    {formatFilterDateOrPlaceholder(listStart ? new Date(`${listStart}T00:00:00`) : null)}
          </button>
                  <span>–</span>
                  <button
                    type="button"
                    className={`custom-date-button${showDatePicker && activeDateField === 'end' ? ' is-active' : ''}`}
                    title={formatFilterDateOrPlaceholder(listEnd ? new Date(`${listEnd}T00:00:00`) : null)}
                    onClick={(e) => {
                      e.stopPropagation();
                      openCustomDatePicker('end', e);
                    }}
                  >
                    {formatFilterDateOrPlaceholder(listEnd ? new Date(`${listEnd}T00:00:00`) : null)}
                        </button>
                              </div>
              )}
                              </div>

            {!globalOn && <span className={s.warn}>{t('ea.tradingOffPlatform')}</span>}

            <button
              type="button"
              className={`${s.newBtn} ${s.toolbarAction}`}
              title={t('ea.newStrategyTitle')}
              onClick={() => openBuilder(null, false)}
            >
              <StrategyBulbIcon className={s.newBtnIcon} /> {t('ea.strategy')}
            </button>
                            </div>

          <div className="table-scroll-area" ref={tableScrollRef}>
            <div className="horizontal-scroll-wrapper">
              <DataTable
                className="history-rdt"
                columns={tableColumns}
                data={filteredRows}
                keyField="id"
                pagination={false}
                theme="solarized"
                customStyles={STRATEGY_TABLE_STYLES}
                persistTableHead
                expandableRows
                expandableRowsHideExpander
                expandableRowExpanded={(row) => expanded === row.id}
                expandableRowsComponent={StrategyJournalPanel}
                conditionalRowStyles={strategyRowStyles}
                onRowClicked={(row) => toggleJournal(row.id)}
                highlightOnHover={false}
                pointerOnHover={false}
                noDataComponent={<div className={s.empty}>{t('ea.noStrategiesMatchFilters')}</div>}
              />
              {filteredRows.length > 0 && (
                <div className="no-more-inline">{t('tables.noMoreStrategies')}</div>
                              )}
                            </div>
                              </div>
        </div>
      )}

      {showDatePicker && (
        <AppDatePicker
          open={showDatePicker}
          activeDateField={activeDateField}
          value={activeDateField === 'start' ? listStart : listEnd}
          minDate={activeDateField === 'end' && listStart ? listStart : undefined}
          anchorRect={pickerAnchorRect}
          buyColor={buyColor}
          sellColor={sellColor}
          onCancel={() => setShowDatePicker(false)}
          onConfirm={(date) => {
            const day = toLocalYmd(date);
            setListPeriod('Custom');
            if (activeDateField === 'start') {
              setListStart(day);
              if (listEnd && day > listEnd) setListEnd(day);
            } else {
              setListEnd(day);
            }
            setShowDatePicker(false);
          }}
        />
      )}

      {createPortal(
        <div className="confirm-box" role={deleteTarget ? 'true' : 'false'}>
          <div className="card">
            <h2>
              {deleteTarget
                ? t('ea.confirmDeleteNamed', { name: deleteTarget.name })
                : t('ea.confirmDelete')}
            </h2>
            <div className="row-scss">
              <button
                type="button"
                onClick={cancelDelete}
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  isolation: 'isolate',
                  backgroundColor: sellColor,
                }}
              >
                <Ripple />
                {t('common.cancel')}
              </button>
              <div className="blank" />
              <button
                type="button"
                onClick={confirmDelete}
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  isolation: 'isolate',
                  backgroundColor: buyColor,
                }}
              >
                <Ripple />
                {t('common.confirm')}
              </button>
                            </div>
                          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
